// ErrorBoundary — captura erros de render em descendentes e mostra fallback
// com opções de recovery (voltar / recarregar) sem deixar a tela em branco.
//
// Sem isso, qualquer throw em render do React mata toda a árvore e a UI fica
// branca — usuário só consegue voltar dando reload. Com boundary, mostramos
// uma tela de erro decente e oferece "Voltar pra Apps" que reinicia o boundary.

import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Override do fallback se quiser custom. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: unknown) {
    // Log pra console pra debug. Em produção poderia enviar pra Sentry/etc.
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  hardReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white border border-red-200 rounded-2xl shadow-sm max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-gray-900">Algo deu errado</h2>
                <p className="text-sm text-gray-600 mt-0.5">A página encontrou um erro e não pôde renderizar.</p>
              </div>
            </div>
            <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded-lg p-2 overflow-x-auto max-h-32 text-gray-700 font-mono mb-4">
              {this.state.error.message}
            </pre>
            <div className="flex gap-2">
              <a
                href="#/apps"
                onClick={this.reset}
                className="flex-1 text-center px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center gap-1.5"
              >
                <Home size={14} /> Voltar pra Apps
              </a>
              <button
                onClick={this.hardReload}
                className="flex-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg inline-flex items-center justify-center gap-1.5"
              >
                <RefreshCw size={14} /> Recarregar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
